import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Heart, Users, Calendar, MessageSquare, Activity, Clock, 
  Phone, Mail, AlertCircle, CheckCircle, User, Send, 
  Home, Pill, Stethoscope, FileText, Bell, RefreshCw,
  Brain, Lightbulb, HelpCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FamilyPortalEnhancedPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('status');
  const [loading, setLoading] = useState(true);
  const [careStatus, setCareStatus] = useState(null);
  const [careUpdates, setCareUpdates] = useState([]);
  const [linkedPatients, setLinkedPatients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  const [careRecommendations, setCareRecommendations] = useState(null);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchLinkedPatients(),
        fetchDashboardStats(),
        fetchCareStatus(),
        fetchCareUpdates(),
        fetchMessages(),
        fetchCareRecommendations()
      ]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedPatients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/phase36/family/linked-patients`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setLinkedPatients(data.patients || []);
        if (data.patients?.length > 0 && !selectedPatient) {
          setSelectedPatient(data.patients[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching linked patients:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/phase36/family/dashboard`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardStats(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchCareStatus = async (patientId = null) => {
    try {
      const url = patientId 
        ? `${API_URL}/api/phase36/family/loved-one/status?patient_id=${patientId}`
        : `${API_URL}/api/phase36/family/loved-one/status`;
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setCareStatus(data);
      }
    } catch (error) {
      console.error('Error fetching care status:', error);
    }
  };

  const fetchCareUpdates = async (patientId = null) => {
    try {
      const url = patientId
        ? `${API_URL}/api/phase36/family/care-updates?patient_id=${patientId}`
        : `${API_URL}/api/phase36/family/care-updates`;
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setCareUpdates(data.updates || []);
      }
    } catch (error) {
      console.error('Error fetching care updates:', error);
    }
  };

  const fetchMessages = async (patientId = null) => {
    try {
      const url = patientId
        ? `${API_URL}/api/phase36/family/messages?patient_id=${patientId}`
        : `${API_URL}/api/phase36/family/messages`;
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchCareRecommendations = async (patientId = null) => {
    setRecommendationsLoading(true);
    try {
      const url = patientId
        ? `${API_URL}/api/phase37/care-recommendations/${patientId}`
        : `${API_URL}/api/phase37/care-recommendations/my/loved-one`;
      const res = await fetch(url, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setCareRecommendations(data);
      }
    } catch (error) {
      console.error('Error fetching care recommendations:', error);
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedPatient) {
      toast.error('Please enter a message');
      return;
    }

    setMessageLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/phase36/family/message/${selectedPatient.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          message: newMessage,
          priority: 'normal'
        })
      });

      if (res.ok) {
        toast.success('Message sent to care team');
        setNewMessage('');
        fetchMessages(selectedPatient.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setMessageLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'stable': return 'bg-green-100 text-green-800';
      case 'intensive_care': return 'bg-amber-100 text-amber-800';
      case 'declining': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getUpdateIcon = (type) => {
    switch (type) {
      case 'visit': return <Stethoscope className="h-4 w-4 text-teal-500" />;
      case 'medication': return <Pill className="h-4 w-4 text-purple-500" />;
      case 'care_plan': return <FileText className="h-4 w-4 text-blue-500" />;
      default: return <Activity className="h-4 w-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6" data-testid="family-portal-enhanced">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Heart className="h-8 w-8 text-rose-500" />
            {t('familyPortalEnhanced.title', 'Family Care Portal')}
          </h1>
          <p className="text-slate-600 mt-2">{t('familyPortalEnhanced.subtitle', "Stay connected with your loved one's care journey")}</p>
        </div>

        {/* Dashboard Stats */}
        {dashboardStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('familyPortalEnhanced.linkedPatients', 'Linked Patients')}</p>
                    <p className="text-2xl font-bold text-slate-800">{dashboardStats.linked_patients}</p>
                  </div>
                  <Users className="h-8 w-8 text-teal-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('familyPortalEnhanced.visitsThisWeek', 'Visits This Week')}</p>
                    <p className="text-2xl font-bold text-green-600">{dashboardStats.visits_this_week}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('familyPortalEnhanced.upcomingVisits', 'Upcoming Visits')}</p>
                    <p className="text-2xl font-bold text-blue-600">{dashboardStats.upcoming_visits}</p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('familyPortalEnhanced.unreadMessages', 'Unread Messages')}</p>
                    <p className="text-2xl font-bold text-purple-600">{dashboardStats.unread_messages}</p>
                  </div>
                  <MessageSquare className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Patient Selector */}
        {linkedPatients.length > 1 && (
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <Label className="text-sm font-medium">{t('familyPortalEnhanced.selectLovedOne', 'Select Loved One')}:</Label>
                <div className="flex gap-2">
                  {linkedPatients.map((patient) => (
                    <Button
                      key={patient.id}
                      variant={selectedPatient?.id === patient.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setSelectedPatient(patient);
                        fetchCareStatus(patient.id);
                        fetchCareUpdates(patient.id);
                      }}
                    >
                      {patient.name}
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {patient.relationship}
                      </Badge>
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm flex-wrap">
            <TabsTrigger value="status" data-testid="tab-status">
              <Heart className="h-4 w-4 mr-2" />
              {t('familyPortalEnhanced.careStatus', 'Care Status')}
            </TabsTrigger>
            <TabsTrigger value="ai-tips" data-testid="tab-ai-tips">
              <Sparkles className="h-4 w-4 mr-2" />
              {t('familyPortalEnhanced.aiCareTips', 'AI Care Tips')}
            </TabsTrigger>
            <TabsTrigger value="updates" data-testid="tab-updates">
              <Activity className="h-4 w-4 mr-2" />
              {t('familyPortalEnhanced.updates', 'Updates')}
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="h-4 w-4 mr-2" />
              {t('familyPortalEnhanced.careTeam', 'Care Team')}
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('familyPortalEnhanced.messages', 'Messages')}
            </TabsTrigger>
          </TabsList>

          {/* AI Care Tips Tab */}
          <TabsContent value="ai-tips">
            {recommendationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
              </div>
            ) : careRecommendations?.recommendations ? (
              <div className="space-y-6">
                {/* Daily Focus Card */}
                <Card className="border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-teal-800">
                      <Lightbulb className="h-5 w-5 text-amber-500" />
                      {careRecommendations.recommendations.daily_focus?.title}
                    </CardTitle>
                    <CardDescription className="text-teal-700">
                      {careRecommendations.recommendations.daily_focus?.message}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {careRecommendations.recommendations.daily_focus?.activities?.map((activity, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-white text-teal-700">
                          {activity}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Personalized Message */}
                <Card className="bg-rose-50 border-rose-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <Heart className="h-6 w-6 text-rose-500 flex-shrink-0 mt-1" />
                      <p className="text-rose-800 text-lg">
                        {careRecommendations.recommendations.personalized_message}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Questions to Ask During Visits */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HelpCircle className="h-5 w-5 text-blue-500" />
                      Questions to Ask During Visits
                    </CardTitle>
                    <CardDescription>
                      Suggested questions based on your loved one&apos;s current care needs
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {careRecommendations.recommendations.visit_questions?.map((q, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                          <Badge 
                            variant={q.priority === 'high' ? 'default' : 'secondary'}
                            className={q.priority === 'high' ? 'bg-red-500' : ''}
                          >
                            {q.priority}
                          </Badge>
                          <div>
                            <p className="font-medium text-slate-800">{q.question}</p>
                            <p className="text-sm text-slate-500">{q.category}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Care Tips */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="h-5 w-5 text-purple-500" />
                      Care Tips
                    </CardTitle>
                    <CardDescription>
                      Practical recommendations for providing comfort
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {careRecommendations.recommendations.care_tips?.map((tip, idx) => (
                        <div key={idx} className="p-4 bg-purple-50 rounded-lg">
                          <Badge variant="outline" className="mb-2 text-purple-600 border-purple-300">
                            {tip.category}
                          </Badge>
                          <p className="text-slate-700">{tip.tip}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Communication Tips */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-green-500" />
                      Communication Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {careRecommendations.recommendations.communication_tips?.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-slate-700">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Resources */}
                {careRecommendations.recommendations.resources?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-orange-500" />
                        Helpful Resources
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {careRecommendations.recommendations.resources.map((cat, idx) => (
                          <div key={idx}>
                            <h4 className="font-semibold text-slate-800 mb-2">{cat.title}</h4>
                            <div className="space-y-2">
                              {cat.resources?.map((res, ridx) => (
                                <div key={ridx} className="flex items-center gap-2 text-sm text-slate-600">
                                  <Badge variant="secondary" className="text-xs">{res.type}</Badge>
                                  <span>{res.name}</span>
                                  {res.phone && <span className="text-blue-600">{res.phone}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Refresh Button */}
                <div className="flex justify-center">
                  <Button 
                    variant="outline" 
                    onClick={() => fetchCareRecommendations(selectedPatient?.id)}
                    disabled={recommendationsLoading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${recommendationsLoading ? 'animate-spin' : ''}`} />
                    Refresh Recommendations
                  </Button>
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Sparkles className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No AI recommendations available yet</p>
                  <p className="text-sm text-slate-400 mt-2">Link a patient to receive personalized care tips</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Care Status Tab */}
          <TabsContent value="status">
            {careStatus ? (
              <div className="space-y-6">
                {/* Status Summary */}
                <Card className={`border-2 ${careStatus.status_summary?.color === 'green' ? 'border-green-200' : careStatus.status_summary?.color === 'amber' ? 'border-amber-200' : 'border-slate-200'}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Heart className="h-5 w-5 text-rose-500" />
                        {careStatus.patient_name || "Your Loved One"}
                      </CardTitle>
                      <Badge className={getStatusColor(careStatus.status_summary?.status)}>
                        {careStatus.status_summary?.description}
                      </Badge>
                    </div>
                    <CardDescription>{careStatus.status_summary?.detail}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Comfort Level</p>
                        <p className="text-lg font-semibold text-slate-800">
                          {careStatus.current_condition?.comfort_level}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Pain Status</p>
                        <p className="text-lg font-semibold text-slate-800">
                          {careStatus.current_condition?.pain_level}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Alertness</p>
                        <p className="text-lg font-semibold text-slate-800">
                          {careStatus.current_condition?.alertness}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Appetite</p>
                        <p className="text-lg font-semibold text-slate-800">
                          {careStatus.current_condition?.appetite}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Mobility</p>
                        <p className="text-lg font-semibold text-slate-800">
                          {careStatus.current_condition?.mobility}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Functional Status */}
                {careStatus.functional_status && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-blue-500" />
                        Functional Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-600">PPS Score</p>
                          <p className="text-2xl font-bold text-blue-800">
                            {careStatus.functional_status.pps_score || 'N/A'}%
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            {careStatus.functional_status.pps_description}
                          </p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                          <p className="text-sm text-purple-600">Care Level</p>
                          <p className="text-xl font-bold text-purple-800">
                            {careStatus.functional_status.care_level}
                          </p>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-600">Last Updated</p>
                          <p className="text-lg font-medium text-slate-800">
                            {careStatus.current_condition?.last_updated 
                              ? new Date(careStatus.current_condition.last_updated).toLocaleDateString()
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Upcoming Visits */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-green-500" />
                        Upcoming Visits
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {careStatus.upcoming_visits?.length > 0 ? (
                        <div className="space-y-3">
                          {careStatus.upcoming_visits.map((visit, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                              <div>
                                <p className="font-medium text-green-800">{visit.type}</p>
                                <p className="text-sm text-green-600">{visit.staff_name}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium text-green-800">{visit.date}</p>
                                <p className="text-sm text-green-600">{visit.time}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No upcoming visits scheduled</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-500" />
                        Recent Visits
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {careStatus.recent_visits?.length > 0 ? (
                        <div className="space-y-3">
                          {careStatus.recent_visits.map((visit, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium text-slate-800">{visit.type}</p>
                                <p className="text-sm text-slate-600">{visit.staff_name}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium text-slate-800">{visit.date}</p>
                                <Badge variant={visit.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                                  {visit.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No recent visits</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Current Medications */}
                {careStatus.current_medications?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Pill className="h-5 w-5 text-purple-500" />
                        Current Medications
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {careStatus.current_medications.map((med, idx) => (
                          <div key={idx} className="p-4 bg-purple-50 rounded-lg">
                            <p className="font-medium text-purple-800">{med.name}</p>
                            <p className="text-sm text-purple-600">{med.dosage} - {med.frequency}</p>
                            {med.purpose && (
                              <p className="text-xs text-purple-500 mt-1">For: {med.purpose}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Heart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No care status information available</p>
                  <p className="text-sm text-slate-400 mt-2">Please contact the care team to link your account</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Updates Tab */}
          <TabsContent value="updates">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-teal-500" />
                  Care Updates Timeline
                </CardTitle>
                <CardDescription>Recent updates about your loved one&apos;s care</CardDescription>
              </CardHeader>
              <CardContent>
                {careUpdates.length > 0 ? (
                  <div className="space-y-4">
                    {careUpdates.map((update, idx) => (
                      <div key={update.id || idx} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                          {getUpdateIcon(update.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-800">{update.title}</p>
                            <span className="text-sm text-slate-500">{update.date}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{update.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No updates available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Care Team Tab */}
          <TabsContent value="team">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-teal-500" />
                  Care Team
                </CardTitle>
                <CardDescription>Your loved one&apos;s dedicated care team</CardDescription>
              </CardHeader>
              <CardContent>
                {careStatus?.care_team?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {careStatus.care_team.map((member, idx) => (
                      <div key={member.id || idx} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                            <User className="h-6 w-6 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{member.name}</p>
                            <p className="text-sm text-slate-500 capitalize">{member.role?.replace('_', ' ')}</p>
                          </div>
                        </div>
                        {member.is_primary && (
                          <Badge className="mb-2 bg-teal-100 text-teal-800">Primary</Badge>
                        )}
                        <div className="space-y-2 text-sm">
                          {member.phone && (
                            <a href={`tel:${member.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-teal-600">
                              <Phone className="h-4 w-4" />
                              {member.phone}
                            </a>
                          )}
                          {member.email && (
                            <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-slate-600 hover:text-teal-600">
                              <Mail className="h-4 w-4" />
                              {member.email}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Care team information not available</p>
                  </div>
                )}

                {/* Emergency Contact */}
                <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="font-semibold text-red-800 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    24/7 Hospice Hotline
                  </h3>
                  <p className="text-red-700 mt-1">For urgent concerns, call:</p>
                  <a href="tel:+17035552273" className="text-2xl font-bold text-red-800 hover:underline">
                    703-578-7108
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Message List */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-purple-500" />
                    Messages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {messages.length > 0 ? (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {messages.map((msg, idx) => (
                        <div 
                          key={msg.id || idx} 
                          className={`p-4 rounded-lg ${msg.type === 'family_to_team' ? 'bg-teal-50 ml-8' : 'bg-slate-50 mr-8'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-slate-800">
                              {msg.type === 'family_to_team' ? 'You' : msg.sender_name || 'Care Team'}
                            </p>
                            <span className="text-xs text-slate-500">
                              {new Date(msg.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-slate-600">{msg.message}</p>
                          {msg.status === 'unread' && msg.type !== 'family_to_team' && (
                            <Badge variant="secondary" className="mt-2 text-xs">New</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No messages yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Send Message */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5 text-teal-500" />
                    Send Message
                  </CardTitle>
                  <CardDescription>Contact the care team</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="message">Your Message</Label>
                      <Textarea
                        id="message"
                        data-testid="input-message"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message to the care team..."
                        rows={6}
                      />
                    </div>
                    <Button 
                      onClick={sendMessage} 
                      disabled={messageLoading || !selectedPatient}
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      data-testid="btn-send-message"
                    >
                      {messageLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send to Care Team
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      Messages are typically responded to within 24 hours. For urgent matters, please call the hotline.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
